import React, {
  memo,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import { Form, Tooltip, Checkbox } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { AgGridReact } from 'ag-grid-react';
import { PopAlert } from 'appkit-react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { typeTextMap } from '@model';
import { Loading } from '@components';
import {
  getModuleListItem,
  getPermissionToAPI,
} from '../../../../../flux/action/projects';
import AgCheckboxEditor from './agGridComponent/AgCheckboxEditor';
import AgColorSelector from './agGridComponent/AgColorSelector';
import ExtendableTextArea from './agGridComponent/extendableTextAreaForAggrid';
import RatioCellRenderer from './agGridComponent/ratioSelector';
import ManageMAdd from './agGridComponent/add-type-modal';

import { DragableTableFrom as FormList, Alert } from '@components';
import {
  INPUT_COMPS,
  validateTrigger,
} from '@components/dragableTableFrom/formItems';
import Label from '@components/formItem/FormLabel';
import { INPUT_MAP } from './config/form.config';
import { FORM_KEYS } from './config/form.keys';
import { getDefaultRowData, columns } from './config/columns.config';
import { addDefaultKey } from '@components/dragableTableFrom/formList/utils';
import { addModuleConfig } from './config/add.config';
import actions from '@flux/action';
import Tab from './Tabs';

// import '@style/modules/project/integrationHub/manageForm.scss';
import '../../../../../styles/modules/project/integrationHub/manageForm.scss';
import PendoIO from '@utils/pendo';
import { errorLog } from '@utils/logEnhance';
import { deepCopy } from '@utils/common';
import { getSessionStorage } from '@utils/windowUtil';
import { SaveAndCancelBtns } from '@components/actionButtonsGroup';

// import 'ag-grid-community/styles/ag-grid.css';
// import 'ag-grid-community/styles/ag-theme-alpine.css';
import './ag-grid.scss';
import { DisplayModeSwitch } from './displayModeSwitch';

const decapitalize = (phrase) =>
  typeof phrase === 'string' && phrase.length > 0
    ? `${phrase.charAt(0).toLowerCase()}${phrase.substring(1)}`
    : '';

const COL_ORDER_KEY = FORM_KEYS.ORDER.key;
const NAVI_NAME_KEY = FORM_KEYS.NAVI_NAME.key;

const NaviInput = INPUT_COMPS[INPUT_MAP[NAVI_NAME_KEY].inputType];

const getDefaultTabIndex = (tabs = []) => {
  const result = tabs.findIndex((tab) => !tab.deletable);
  return result >= 0 ? result : 0;
};

const scrollYSync = () => {
  setTimeout(() => {
    window.scrollBy({ top: -64 });
  });
};

const getDefaultTableData = () =>
  [1].map(() => getDefaultRowData({ display: true }));

const getFirstErrorTab = (errorFields, tabList = [], currentTab) => {
  let result = '';
  const errTabs = Array.from(
    new Set(errorFields.map((field) => field.name[0])),
  );

  if (errTabs.includes(currentTab)) {
    return currentTab;
  }

  for (const tab of tabList) {
    if (errTabs.includes(tab.id)) {
      result = tab.id;

      break;
    }
  }

  return result;
};

const getFirstErrorField = (errorFields, firstFieldTab) =>
  errorFields.find((f) => f.name[0] === firstFieldTab)?.name || [];

function ManageMForm({
  saveData,
  integrationNavigatorName,
  displayMode,
  tabs,
  integrationModuleData,
  customSaveData,
  afterSaveData,
  goBack: goBackInProps,
  changeCurrentTab,
  currentTab: currentTabInProps,
  type, // if using for project is undefined, if using system setting is project instance ID
  showSpn,
  groupInfo = [],
  cRef,
  pendoField,
  projectId,
  userId,
  userName,
  formChange = () => {},
  wrapperContainerClass,
}) {
  const gridRefs = useRef({});
  const [form] = Form.useForm();
  const history = useHistory();
  const [actionLoading, toggleActionLoading] = useState(false);
  const [currentTabState, toggleTab] = useState({
    currentTabId: currentTabInProps || '',
    tabList: [],
  });
  const [refreshCount, setRefreshCount] = useState({});
  const fieldMethods = useRef({});
  const { currentTabId, tabList } = currentTabState;
  const currentTabIdRef = useRef(currentTabId);
  const cumulateRefreshCount = () =>
    setRefreshCount((c) => ({
      ...c,
      [currentTabIdRef.current]: (c[currentTabIdRef.current] || 0) + 1,
    }));
  const [rowData, setRowData] = useState([]);
  // Column Definitions: Defines the columns to be displayed.
  const handleAddRow = async (type) => {
    const newId = uuidv4();
    const newRow = {
      ...(getDefaultRowData?.(await addDefaultKey(type)) || {}),
      moduleId: newId,
      id: newId,
    };
    const currentGridRef = gridRefs?.current[currentTabIdRef?.current];
    const data = form.getFieldsValue()[currentTabIdRef.current] || [];
    // currentGridRef.current.api.applyTransaction({
    //   add: [newRow],
    //   addIndex: data.length - 1,
    // });
    data.push(newRow);
    form.setFieldsValue({
      ...form.getFieldsValue(),
      [currentTabIdRef.current]: data,
    });
    setRowData(data);
    cumulateRefreshCount();
    // currentGridRef.current.api.ensureNodeVisible(newRow, 'bottom');
    formChange();
  };
  const handleRemoveRow = (params) => {
    const moduleId = params.data.moduleId
      ? params.data.moduleId
      : params.data.id;
    const currentGridRef = gridRefs?.current[currentTabIdRef?.current];
    // currentGridRef.current.api.applyTransaction({
    //   remove: [{ moduleId }],
    // });
    const resData =
      form.getFieldsValue()[currentTabIdRef?.current]?.filter?.((item) => {
        const id = item.moduleId ? item.moduleId : item.id;
        return id !== moduleId;
      }) || [];
    setRowData(resData);
    form.setFieldsValue({ [currentTabIdRef?.current]: resData });
    formChange();
  };
  const defaultCellRenderer = (params) => {
    if (params.checkbox) {
      return <Checkbox defaultChecked={params.value} disabled />;
    }
    if (params.richText) {
      return (
        <div
          className="cell-text"
          dangerouslySetInnerHTML={{ __html: params.value }}
        />
      );
    }
    return <div className="cell-text">{params.value || null}</div>;
  };

  const removeCellRenderer = (params) => (
    <div className="romove-button" onClick={() => handleRemoveRow(params)} />
  );
  const afterChange = (id, newRow) => {
    const currentGridRef = gridRefs?.current[currentTabIdRef?.current];
    const resData = form
      .getFieldsValue()
      [currentTabIdRef.current]?.map?.((item) => {
        if (item.moduleId === id || item.id === id) {
          return { ...item, ...newRow };
        }
        return item;
      });
    form.setFieldsValue({
      ...form.getFieldsValue(),
      [currentTabIdRef.current]: resData,
    });
    setRowData(resData);
    // currentGridRef.current.api.applyTransaction({
    //   update: [{ moduleId: id, id }],
    // });
    formChange();
  };
  const linkCellRenderer = (params) => {
    const type = params?.data?.type;

    return (
      <ExtendableTextArea
        type={type}
        shouldExtend
        autoSize
        formChange={formChange}
        afterChange={afterChange}
        {...params}
      />
    );
  };
  const [colDefs, setColDefs] = useState([
    {
      field: [FORM_KEYS.MODULE.key],
      headerName: [FORM_KEYS.MODULE.label],
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 50,
      },
      valueSetter: (params) => {
        const newValue = params.newValue;
        if (!newValue || newValue.length > 50) {
          return false;
        }
        const currentTabData = form.getFieldsValue()[currentTabIdRef.current];
        // 检查重复的逻辑
        if (currentTabData.some((row) => row.module === newValue)) {
          PopAlert({
            className: 'success-message-popover',
            oneLineDisplay: true,
            duration: 2000,
            status: 'error',
            message: 'Duplicate tile name(s)',
          });
          return false;
        }
        params.data[FORM_KEYS.MODULE.key] = newValue;
        afterChange(params.data.moduleId || params.data.id, {
          [FORM_KEYS.MODULE.key]: newValue,
        });
        return true;
      },
      rowDrag: true,
      cellRenderer: defaultCellRenderer,
    },
    {
      field: [FORM_KEYS.TYPE.key],
      headerName: [FORM_KEYS.TYPE.label],
      editable: false,
      width: 120,
      cellClass: ['canot-edit-field'],
      valueGetter: (params) =>
        typeTextMap.find((item) => item.value === params?.data?.type)
          ?.typeText || 'Other',
      cellRenderer: defaultCellRenderer,
    },
    {
      field: [FORM_KEYS.DISPLAY.key],
      headerName: [FORM_KEYS.DISPLAY.label],
      width: 100,
      editable: false,
      cellRendererSelector: (params) => {
        const disabled = [
          'imageModule',
          'widget',
          'multiSection',
          // 'powerbi',
          // 'powerbiModule',
        ].includes(params?.data?.type);
        const resSelector = {
          component: AgCheckboxEditor,
          params: {
            checkbox: true,
            afterChange,
            disabled,
          },
        };
        return resSelector;
      },
    },
    {
      field: [FORM_KEYS.IFRAME.key],
      headerName: 'iframe',
      width: 100,
      headerComponent: () => (
        <>
          Iframe
          <Tooltip
            placement="right"
            title={() => (
              <div>
                iFrame allows you to embed other webpages, documents, and other
                forms of multimedia without leaving the page. Please refer to
                the iFrame configuration{' '}
                <a
                  target="_blank"
                  href="/gateway/guide-shared"
                  style={{ padding: 0, fontSize: '1rem' }}
                >
                  <span style={{ textDecoration: 'underline' }}>here</span>
                </a>{' '}
                for additional instructions for PowerBI and Tableau.
              </div>
            )}
            className="iframe-col-tooltip"
          >
            <span
              className="appkiticon icon-information-fill "
              style={{ marginLeft: '0.313rem' }}
            />
          </Tooltip>
        </>
      ),
      editable: false,
      cellRendererSelector: (params) => {
        const disabled = !['text'].includes(params?.data?.type);
        // console.log('params?.data?.type: ', params?.data)
        const resSelector = {
          component: AgCheckboxEditor,
          params: {
            checkbox: true,
            afterChange,
            disabled,
          },
        };
        return resSelector;
      },
    },
    {
      field: [FORM_KEYS.COLOR.key],
      headerName: [FORM_KEYS.COLOR.label],
      width: 110,
      editable: false,
      cellClass: ['no-padding'],
      cellRendererParams: {
        afterChange,
      },
      cellRenderer: AgColorSelector,
    },
    {
      field: ['ratio'],
      headerName: 'Ratio',
      width: 160,
      editable: false,
      cellClass: ['no-padding'],
      cellRendererParams: {
        afterChange,
      },
      cellRenderer: RatioCellRenderer,
    },
    {
      field: [FORM_KEYS.LINK.key],
      headerName: [FORM_KEYS.LINK.label],
      cellRenderer: linkCellRenderer,
    },
    {
      field: [FORM_KEYS.DES.key],
      headerName: [FORM_KEYS.DES.label],
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      cellEditorParams: {
        afterChange,
        maxLength: 180,
        className: 'description-fixed-240px',
      },
      valueGetter: (params) => params.data?.[FORM_KEYS.DES.key] || '',
      valueSetter: (params) => {
        const newValue = params.newValue || ''; // Ensure it's never undefined
        if (newValue.length > 180) {
          return false; // Reject if too long
        }
        params.data[FORM_KEYS.DES.key] = newValue;
        afterChange(params.data.moduleId || params.data.id, {
          [FORM_KEYS.DES.key]: newValue,
        });
        return true;
      },
      cellRenderer: defaultCellRenderer,
      cellClass: ['description-cell'],
      flex: 1,
    },
    {
      headerName: '',
      field: 'action',
      width: 60,
      editable: false,
      resizable: false,
      suppressMovable: true,
      lockPosition: 'right',
      cellRenderer: removeCellRenderer,
    },
  ]);
  const defaultColDef = useMemo(
    () => ({
      editable: true,
      colSpan: (params) => 1,

      suppressMovable: true,
    }),
    [],
  );

  const [listDisplayMode, setListDisplayMode] = useState(
    displayMode === true ? true : displayMode !== 'Tile',
  );

  useImperativeHandle(cRef, () => ({
    getForm: () => form,
    getData: () => {
      const formData = form.getFieldsValue();
      return {
        integrationName: formData.integrationNavigatorName,
        integrationTabs: (currentTabState?.tabList || []).map((tab) => ({
          tabName: tab.text,
          isDefaultTab: tab.deletable ? 0 : 1,
          moduleList: getModuleListItem(formData[tab.id]),
          permission: getPermissionToAPI(tab.permission),
        })),
        displayMode: listDisplayMode ? 'List' : 'Tile',
      };
    },
  }));
  const registerFieldsMethods = (methods) => {
    fieldMethods.current = methods;
  };

  const handleFieldsChange = (...args) => {
    validateTrigger(...args, form, COL_ORDER_KEY, currentTabId);
  };

  const handleFieldsFail = ({ errorFields, _firstErrorTab, ...rest }) => {
    if (Array.isArray(errorFields)) {
      const firstErrorTab = getFirstErrorTab(
        errorFields,
        tabList || tabs,
        currentTabId,
      );

      const firstFieldOfTab = getFirstErrorField(errorFields, firstErrorTab);

      if (firstFieldOfTab.length > 0) {
        toggleTab((state) => ({
          ...state,
          currentTabId: firstErrorTab,
        }));
        let alertMsg = 'Please check Tile Name field.';
        const firstError = errorFields[0];
        if (
          firstError?.errors &&
          firstError?.name &&
          firstError.name[2] === 'description'
        ) {
          alertMsg = `The ${firstError.name[2]} in row ${
            firstError.name[1] + 1
          } ${decapitalize(firstError.errors[0])}`;
        }
        currentTabIdRef.current = firstErrorTab;
        Alert.error(alertMsg);
        form.scrollToField(firstFieldOfTab, { inline: 'start' });
        scrollYSync();
      }
    }
    toggleActionLoading(false);
  };

  const goBack = () => {
    history.replace(`/project-detail/${getSessionStorage('currentId')}`);
  };

  const pendoCommon = {
    project_id: projectId,
    uid: userId,
    full_name: userName,
  };

  const matchTabById = (tList, tId) => {
    for (const i in tList) {
      if (tList[i].id === tId) {
        return tList[i];
      }
    }
  };

  const handleTrack = (a, b, tabData) => {
    const prevTab = tabs.map((tab) => tab.id);
    const currTab = currentTabState?.tabList?.map((tab) => tab.id);

    const addTab =
      currTab === undefined
        ? []
        : currTab
            .map((value) => {
              if (!prevTab.includes(value)) {
                return {
                  id: value,
                  name: matchTabById(currentTabState.tabList, value).text,
                };
              }
            })
            .filter(Boolean);

    const deleteTab = prevTab
      .map((value) => {
        if (currTab != undefined && !currTab.includes(value)) {
          return { id: value, name: matchTabById(tabs, value).text };
        }
      })
      .filter(Boolean);

    const editTab = [];
    for (let i = 0; i < tabData.length; i++) {
      for (let j = 0; j < currentTabState?.tabList?.length; j++) {
        if (
          tabData[i].id === currentTabState?.tabList[j]?.id &&
          tabData[i].text != currentTabState?.tabList[j]?.text
        ) {
          editTab.push({
            id: currentTabState?.tabList[j]?.id,
            name: currentTabState?.tabList[j]?.text,
          });
        }
      }
    }
    for (const i in addTab) {
      PendoIO.track('Manage Tiles - Add Tab', {
        tab_name: addTab[i].name,
        ...pendoCommon,
      });
    }
    for (const i in deleteTab) {
      PendoIO.track('Manage Tiles - Delete Tab', {
        tab_name: deleteTab[i].name,
        ...pendoCommon,
      });
    }
    for (const i in editTab) {
      PendoIO.track('Manage Tiles - Edit Tab', {
        tab_name: editTab[i].name,
        ...pendoCommon,
      });
    }

    const prevData = deepCopy(a);
    const currData = deepCopy(b);
    delete currData.integrationNavigatorName;
    let prevTiles = [];
    let currTiles = [];
    for (const i in prevData) {
      if (prevData[i].length > 0) {
        prevTiles = prevTiles.concat(prevData[i]);
      }
    }
    for (const i in currData) {
      if (currData[i].length > 0) {
        currTiles = currTiles.concat(currData[i]);
      }
    }
    const prev = prevTiles.map((i) => JSON.stringify(i));
    const curr = currTiles.map((i) => JSON.stringify(i));

    let addData = curr
      .map((value, index) => {
        if (!prev.includes(value)) {
          return currTiles[index];
        }
      })
      .filter(Boolean);

    let deleteData = prev
      .map((value, index) => {
        if (!curr.includes(value)) {
          return prevTiles[index];
        }
      })
      .filter(Boolean);

    let editData = [];
    for (let i = 0; i < addData.length; i++) {
      for (let j = 0; j < deleteData.length; j++) {
        if (!addData[i].moduleId) {
          continue;
        }
        if (addData[i].moduleId === deleteData[j].moduleId) {
          editData.push(addData[i]);
        }
      }
    }
    editData = [...new Set(editData)];

    const edita = editData.map((value) => value.moduleId);
    addData = addData.filter((value) => {
      if (!value.moduleId) {
        return true;
      }
      return !edita.includes(value.moduleId);
    });
    deleteData = deleteData.filter((value) => !edita.includes(value.moduleId));

    for (const i in addData) {
      PendoIO.track('Manage Tiles - Add Tile', {
        tile_name: addData[i].module,
        ...pendoCommon,
      });
    }
    for (const i in deleteData) {
      PendoIO.track('Manage Tiles - Delete Tile', {
        tile_name: deleteData[i].module,
        ...pendoCommon,
      });
    }
    for (const i in editData) {
      PendoIO.track('Manage Tiles - Edit Tile', {
        tile_name: editData[i].module,
        ...pendoCommon,
      });
    }

    PendoIO.track('Manage Tiles - Save', pendoCommon);
  };

  const handleSave = async (e) => {
    toggleActionLoading(true);
    const res = { ...e };
    const _saveData = saveData;

    const _afterSaveData = afterSaveData || goBack;

    const result = await _saveData({
      data: res,
      tabLists: currentTabState.tabList,
      displayMode: listDisplayMode ? 'List' : 'Tile',
    });

    const cachedResult = { success: true };

    if (result?.success && cachedResult.success) {
      _afterSaveData();
      PopAlert({
        className: 'success-message-popover',
        oneLineDisplay: true,
        duration: 2000,
        status: 'success',
        message: 'SAVED SUCCESSFULLY',
      });
    }
    toggleActionLoading(false);
  };

  const handleSortEnd = ({ newIndex, oldIndex }) => {
    const { move } = fieldMethods.current;

    if (move) {
      move(oldIndex, newIndex);
      formChange();
    }
  };

  const handleSubmitForm = () => {
    toggleActionLoading(true);
    form.validateFields().then(handleSave).catch(handleFieldsFail);
    if (pendoField === 'ManageTiles') {
      try {
        handleTrack(integrationModuleData, form.getFieldsValue(), tabs);
      } catch (e) {
        errorLog([e]);
      }
    }
  };

  const handleTabChange = async ({ currentTab, tabs, duplicateFrom }) => {
    if (!currentTab) {
      currentTabIdRef.current = '';
      return toggleTab({
        currentTabId: '',
        tabList: tabs,
      });
    }

    const { id } = currentTab;
    currentTabIdRef.current = id;
    toggleTab({
      currentTabId: id,
      tabList: tabs,
    });

    if (typeof duplicateFrom === 'number') {
      const duplicateData = form.getFieldValue(tabs[duplicateFrom].id);
      if (Array.isArray(duplicateData)) {
        duplicateData.forEach((i) => {
          i.tabeId = id;
          Reflect.deleteProperty(i, 'moduleId');
        });
      }
      form.setFieldsValue({ [id]: duplicateData });
      setRowData(duplicateData);
    }

    changeCurrentTab(id);
  };

  const data = {
    type: type !== 'Integration',
    [NAVI_NAME_KEY]: integrationNavigatorName,
    ...integrationModuleData,
  };

  useEffect(() => {
    let resCurrentTabId;
    if (tabs.length > 0) {
      resCurrentTabId = currentTabInProps || tabs[getDefaultTabIndex(tabs)].id;
    } else if (tabs.length === 0) {
      resCurrentTabId = currentTabInProps;
    }
    toggleTab({
      currentTabId: resCurrentTabId,
    });
    currentTabIdRef.current = resCurrentTabId;

    form.setFieldsValue(data);
    setRowData(data[currentTabId]);
  }, [tabs]);

  useEffect(() => {
    if (!gridRefs?.current[currentTabIdRef?.current]) {
      gridRefs.current[currentTabIdRef?.current] = React.createRef();
    }
    function handleOutsideClick(event) {
      const gridElements = document.querySelectorAll(
        '.ag-center-cols-container',
      );
      const clickOutside = Array.from(gridElements).every(
        (gridElement) => !gridElement.contains(event.target),
      );

      const gridPopupEditor = document.querySelector('.ag-popup-editor');
      // Prevent from mis-stopediting when click a popup editor
      const clickOnTheDesc =
        gridPopupEditor && gridPopupEditor.contains(event.target);
      if (clickOutside && !clickOnTheDesc) {
        // 如果点击发生在网格之外且不是desc那个输入框
        const currentGridRef = gridRefs?.current[currentTabIdRef?.current];
        if (currentGridRef.current) {
          currentGridRef.current.api &&
            currentGridRef.current.api.stopEditing &&
            currentGridRef.current.api.stopEditing();
        }
      }
    }
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [gridRefs.current, currentTabIdRef.current]);

  useLayoutEffect(() => {
    const currentFormData = { ...form.getFieldsValue() };

    if (currentFormData[currentTabId]) {
      setRowData(currentFormData[currentTabId]);
      return;
    }
    setRowData(getDefaultTableData());
    form.setFieldsValue({ [currentTabId]: getDefaultTableData() });
  }, [currentTabId]);

  const TemplateHead = () => (
    <div className="tile-header">
      <div className="right integration-header">
        <NaviInput
          name={NAVI_NAME_KEY}
          required={false}
          rules={INPUT_MAP[NAVI_NAME_KEY].rules}
          colon={false}
          label={<Label text="Navigator Name" />}
          initialValue={integrationNavigatorName}
          inputProps={{
            disabled: actionLoading,
            className: 'integration-nav-name-input',
            maxLength: 40,
            onChange: formChange,
          }}
        />

        <DisplayModeSwitch
          defaultChecked={displayMode === 'Tile' ? false : true}
          onChange={(checked) => {
            setListDisplayMode(checked);
          }}
        />
      </div>
    </div>
  );
  const onRowDragEnd = (rowInfo) => {
    const currentTabData = form.getFieldsValue()[currentTabId] || [];
    function updateDataOrder(data, fromIndex, toIndex) {
      // 检查边界条件
      if (
        fromIndex < 0 ||
        fromIndex >= data.length ||
        toIndex < 0 ||
        toIndex >= data.length
      ) {
        console.error('Invalid indices');
        return;
      }

      // 取出要移动的元素
      const [movedItem] = data.splice(fromIndex, 1); // 从数据中移除该元素

      // 在新位置插入元素
      data.splice(toIndex, 0, movedItem);

      // 更新 order 字段
      data.forEach((item, index) => {
        item.order = index;
      });

      setRowData(data);
      form.setFieldsValue({ [currentTabId]: data });
    }
    const fromIndex = currentTabData.findIndex(
      (item) => item.moduleId === rowInfo.node.data.moduleId,
    );
    const toIndex = rowInfo.overIndex;

    updateDataOrder(currentTabData, fromIndex, toIndex);
  };
  if (actionLoading) {
    return (
      <div className="grid-display-switch-wrapper">
        <Loading style={{ textAlign: 'center', marginTop: '30%' }} />
      </div>
    );
  }
  console.log('Editable value logs : ', actionLoading);
  return (
    <Form
      form={form}
      className="integration-manage-form"
      onValuesChange={handleFieldsChange}
      scrollToFirstError
      autoComplete="off"
      refreshCount={refreshCount[currentTabId]}
    >
      {!customSaveData ? (
        <div className="integration-header">
          <NaviInput
            name={NAVI_NAME_KEY}
            required={false}
            rules={INPUT_MAP[NAVI_NAME_KEY].rules}
            colon={false}
            label={<Label text="Navigator Name" />}
            initialValue={integrationNavigatorName}
            inputProps={{
              disabled: actionLoading,
              className: 'integration-nav-name-input',
              maxLength: 40,
              onChange: formChange,
            }}
          />

          <DisplayModeSwitch
            defaultChecked={displayMode === 'Tile' ? false : true}
            onChange={(checked) => {
              setListDisplayMode(checked);
            }}
          />
        </div>
      ) : (
        <TemplateHead />
      )}
      <Tab
        onChange={handleTabChange}
        tabs={tabs}
        currentTab={currentTabId}
        editable={!actionLoading}
        groupInfo={groupInfo}
        tabTriggerAction={formChange}
        componentType={type} // if using for project is undefined, if using system setting is project instance ID
      />
      <div className="dragable-table-form " key={currentTabId}>
        {(tabList || tabs).map((tab = {}) => (
          <>
            <div
              className="ag-theme-alpine"
              style={{ display: tab.id === currentTabId ? 'block' : 'none' }}
            >
              <AgGridReact
                key={`ag-grid-${tab.id}-${refreshCount[currentTabId] || 0}`}
                ref={gridRefs.current[tab.id]}
                getRowId={(params) =>
                  params?.data?.moduleId || params?.data?.id
                }
                rowBuffer={70}
                rowHeight={36}
                headerHeight={25}
                rowDragManaged={true}
                suppressColumnMove={true}
                // rowData={form.getFieldsValue()[currentTabId]}
                rowData={rowData}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                suppressRowVirtualisation={false}
                onRowDragEnd={onRowDragEnd}
              />
              <ManageMAdd
                text="Add New Tile"
                configs={{ addModuleConfig }}
                showSpn={showSpn}
                onClick={handleAddRow}
              />
            </div>
            <div style={{ display: 'none' }}>
              <FormList
                columns={columns}
                registerFieldsMethods={registerFieldsMethods}
                onSortEnd={handleSortEnd}
                isLoading={actionLoading}
                key={`formlist-${tab.id}`}
                formInstance={form}
                name={tab.id}
                isActive={tab.id === currentTabId}
                getDefaultRowData={getDefaultRowData}
                addBtnText="Add New Tile"
                configs={{ addModuleConfig }}
                showSpn={showSpn}
                // pendoField={pendoField}
                formChange={formChange}
                wrapperContainerClass={wrapperContainerClass}
              />
            </div>
          </>
        ))}
      </div>
      {customSaveData ? null : (
        <div className="action-wrapper">
          <SaveAndCancelBtns
            handleBack={goBackInProps || goBack}
            handleSave={handleSubmitForm}
          />
        </div>
      )}
    </Form>
  );
}

ManageMForm.propTypes = {};

const mapStateToProps = ({ common, login }) => ({
  projectId: common.currentId,
  userId: login.userInfo.guid,
  userName: `${login.userInfo.given_name} ${login.userInfo.family_name}`,
});

const mapDispatchToProps = (dispatch) => ({
  changeCurrentTab(currentTab) {
    dispatch(actions.saveProjectDetail({ currentTab }));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(ManageMForm));
